import { useLookupIP } from "@outschool/iplookup-client";
import { useLocalStorageState } from "@outschool/local-storage";
import { useSession } from "@outschool/ui-auth";
import {
  useExperiment,
  useServerExperimentContext,
} from "@outschool/ui-experiments";
import React from "react";
const EXPERIMENT_NAME = "LoggedOutMemberships";

const EXCLUDED_STATES = ["FL", "AZ"];

const LOCAL_STORAGE_KEY = "LoggedOutMemberships.ExperimentTriggered";

export function useLoggedOutMembershipsExperiment() {
  const { currentUserHasLoaded, isLoggedIn, currentUser, isEsaSession } =
    useSession();

  const { ipInfo, ipInfoLoaded } = useLookupIP();

  const [previouslyTriggeredVariant, setPreviouslyTriggeredVariant] =
    useLocalStorageState<string | undefined>(LOCAL_STORAGE_KEY, undefined);

  const stateIsoCode = ipInfo?.subdivisions?.[0]?.isoCode;
  const isStateExcluded =
    stateIsoCode && EXCLUDED_STATES.includes(stateIsoCode);

  const currentUserRequiresMembershipForEnrollment =
    !!currentUser?.requiresMembershipForEnrollment;

  const { trigger, variant: loggedOutMembershipVariant } = useExperiment({
    currentUserHasLoaded,
    experimentName: EXPERIMENT_NAME,
  });

  const { isTreatment: isTreatmentServerSide } =
    useLoggedOutMembershipsServersideExperiment();

  React.useEffect(() => {
    if (
      !isLoggedIn &&
      ipInfoLoaded &&
      !isStateExcluded &&
      previouslyTriggeredVariant !== loggedOutMembershipVariant
    ) {
      trigger();
      setPreviouslyTriggeredVariant(loggedOutMembershipVariant);
    }
  }, [
    trigger,
    isLoggedIn,
    ipInfoLoaded,
    isStateExcluded,
    previouslyTriggeredVariant,
    loggedOutMembershipVariant,
    setPreviouslyTriggeredVariant,
  ]);

  // TODO: how do we handle rolling back the experiment?
  const requiresMembershipForEnrollment =
    (isLoggedIn
      ? currentUserRequiresMembershipForEnrollment
      : isTreatmentServerSide || loggedOutMembershipVariant === "treatment") &&
    !isEsaSession &&
    !isStateExcluded;

  return {
    requiresMembershipForEnrollment,
  };
}

const useLoggedOutMembershipsServersideExperiment = () => {
  const { experimentAssignments } = useServerExperimentContext();
  const variant = experimentAssignments[EXPERIMENT_NAME];
  const isTreatment = variant === "treatment";
  return {
    isTreatment,
  };
};

export function useShowCredits() {
  const { requiresMembershipForEnrollment } =
    useLoggedOutMembershipsExperiment();
  const { currentUser } = useSession();
  return (
    requiresMembershipForEnrollment ||
    currentUser?.canPayWithSubscriptionCredits
  );
}
